import { useNavigate, useParams } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState } from "react"
import BaseWrapper from "../../components/BaseWrapper/BaseWrapper"
import OpportunityOverview from "../../components/BaseSingleOpportunity/OpportunityOverview";
import OpportunityLines from "../../components/BaseSingleOpportunity/OpportunityLines";
import { Alert, Box, Button, CircularProgress, Tab, Tabs, Typography } from "@mui/material";
import OpportunityLogsModal from "../../components/BaseSingleOpportunity/OpportunityLogsModal";
import OpportunityCompletionStatus from "../../components/BaseSingleOpportunity/OpportunityCompletionStatus";
import Feedback from "../../components/Feedback/Feedback"
import { API } from "../../lib/api"
import { AddCircle, HistoryToggleOff, Person, RequestQuote, InfoOutlined, PaymentsOutlined, FolderOutlined } from "@mui/icons-material";
import UniqueQuote from "../UniqueQuote/UniqueQuote";
import OpportunityNotes from "../../components/BaseSingleOpportunity/OpportunityNotes";
import OpportunityFileUploader from "../../components/OpportunityFileUploader";
import { FormProvider } from "../../contexts/CreateRecordFormContext";
import { useAuth } from "../../contexts/AuthContext";

const canCreateQuotes = (user) => Boolean(user)
const BaseSingleOpportunity = () => {
    const { pid } = useParams();
    const [project, setProject] = useState()
    const [tab, setTab] = useState(0)
    const [quoteVisited, setQuoteVisited] = useState(false)
    const [loadError, setLoadError] = useState(false)
    const [logs, setLogs] = useState([])
    const [feedback, setFeedback] = useState({ open: false, message: "", type: "" })
    const [logModal, setLogModal] = useState(false)
    const [creatingQuote, setCreatingQuote] = useState(false)
    const creatingQuoteRef = useRef(false)
    const requestSequence = useRef(0)
    const { user } = useAuth()

    const navigate = useNavigate();
    const userCanCreateQuotes = canCreateQuotes(user)
    const showCreateQuoteButton = userCanCreateQuotes && !!project?._id && !project?.quoteId

    const handleClose = () => setLogModal(false)

    const handleCustomerNavigate = () => {
        const cid = project?.customer?._id
        if (cid) {
            navigate(`/customers/${cid}`);
        } else {
            setFeedback({ open: true, message: "Cannot Find Customer ID...", type: "error" })
        }
    }


    const handleCreateQuote = async () => {
        if (creatingQuoteRef.current) return
        if (!project?._id) {
            setFeedback({ open: true, message: "Opportunity data is still loading...", type: "error" })
            return
        }

        if (!project?.owner?._id) {
            setFeedback({ open: true, message: "Opportunity owner is required before creating a quote.", type: "error" })
            return
        }

        if (!project?.customer?._id) {
            setFeedback({ open: true, message: "Customer is required before creating a quote.", type: "error" })
            return
        }


        creatingQuoteRef.current = true
        setCreatingQuote(true)
        try {
            const response = await API.Quotes.Create({
                salesManager: project.owner._id,
                opportunityId: project._id,
                customerId: project.customer._id,
            })

            if (response.status === 200) {
                const createdQuote = response?.data?.data
                setProject((prevProject) => prevProject ? {
                    ...prevProject,
                    quoteId: createdQuote?._id || prevProject.quoteId,
                    quoteNumber: createdQuote?.quoteNumber || prevProject.quoteNumber,
                } : prevProject)
                setTab(2)
                setQuoteVisited(true)
                setFeedback({ open: true, message: response.data.message, type: "success" })
            } else {
                setFeedback({ open: true, message: response?.data?.message || "Error Creating Quote...", type: "error" })
            }
        } catch (error) {
            setFeedback({ open: true, message: "Unable to create quote. Please try again.", type: "error" })
        } finally {
            creatingQuoteRef.current = false
            setCreatingQuote(false)
        }
    }

    const refreshOpportunity = useCallback(async () => {
        const sequence = ++requestSequence.current;
        const [response, oppLogsResponse] = await Promise.all([
            API.Opportunities.FetchById(pid), API.OpportunityLogs.FetchByOppId(pid),
        ]);
        if (sequence !== requestSequence.current) return;
        if (!response?._id) throw new Error('Opportunity could not be loaded.');
        setProject(response);
        setLogs(oppLogsResponse || []);
    }, [pid]);

    useEffect(() => {
        setProject(undefined);
        setTab(0);
        setQuoteVisited(false);
        setLoadError(false);
        refreshOpportunity().catch(() => setLoadError(true));
        return () => { requestSequence.current += 1; };
    }, [refreshOpportunity]);

    const selectTab = (_, next) => {
        setTab(next);
        if (next === 2) setQuoteVisited(true);
        if (next === 0 || next === 1) refreshAfterQuoteSave();
    };
    const refreshAfterQuoteSave = useCallback(() => {
        refreshOpportunity().catch(() => setFeedback({ open: true, type: 'error', message: 'Unable to refresh opportunity. Please reload.' }));
    }, [refreshOpportunity]);
    const quoteId = project?.quoteId?._id || project?.quoteId;
    const sharedProps = { project, setProject, setLogs, setFeedback };
    const panels = [
        <><OpportunityCompletionStatus {...sharedProps} pid={pid} /><OpportunityOverview {...sharedProps} /></>,
        <OpportunityLines {...sharedProps} />,
        quoteId ? (quoteVisited && <UniqueQuote key={quoteId} quoteId={quoteId} projectNumber={project?.projectNumber} embedded onSaved={refreshAfterQuoteSave}
            onRevisionCreated={newId => { setProject(previous => ({ ...previous, quoteId: newId })); refreshAfterQuoteSave(); }} />)
            : <Box className="card" sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h6">No quote yet</Typography>
                <Typography color="text.secondary" sx={{ mt: 1 }}>Create a quote using the button above. Its number will be generated automatically.</Typography>
            </Box>,
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 3, alignItems: 'start' }}>
            <OpportunityNotes {...sharedProps} />
            <OpportunityFileUploader opportunityId={pid} opportunity={project} setOpportunity={setProject} setMasterFeedback={setFeedback} fullWidth />
        </Box>,
    ];

    return (
        <BaseWrapper
            content={
                <FormProvider>
                    <>
                        <Feedback props={feedback} setFeedback={setFeedback} />
                        <div className="row hive-single-quote-page">

                            <div className="hive-single-quote-actions" style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", marginTop: "2em", marginBottom: "2em" }}>
                                <Button variant="outlined" onClick={() => setLogModal(true)}>
                                    <HistoryToggleOff /> History
                                </Button>
                                <Button variant="outlined" color="warning" sx={{ ml: 1 }} onClick={handleCustomerNavigate}>
                                    <Person /> View Customer
                                </Button>
                                {showCreateQuoteButton && (
                                    <Button variant="outlined" sx={{ ml: 1 }} onClick={handleCreateQuote} disabled={creatingQuote}>
                                        <AddCircle /> {creatingQuote ? "Creating Quote..." : "Create Quote"}
                                    </Button>
                                )}
                                {project?.quoteId && (
                                    <Button className="hive-single-quote-action" variant="outlined" color="secondary" sx={{ marginLeft: "0.5em" }} onClick={() => selectTab(null, 2)}>
                                        <RequestQuote />
                                        Open Linked Quote
                                    </Button>
                                )}
                            </div>
                            <OpportunityLogsModal logs={logs} open={logModal} handleClose={handleClose} />
                            {loadError ? <Alert severity="error">Unable to load this opportunity. Please reload to try again.</Alert> : !project ?
                                <Box sx={{ p: 5, textAlign: 'center' }}><CircularProgress /></Box> : <Box sx={{ width: '100%', minWidth: 0 }}>
                                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                                    <Tabs value={tab} onChange={selectTab} aria-label="opportunity tabs" variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile
                                        sx={{ '& .MuiTab-root': { flexShrink: 0, minWidth: { xs: 120, sm: 160 } } }}>
                                        {['Overview', 'Revenue Lines', 'Quote', 'Notes & Files'].map((label, index) =>
                                            <Tab key={label} label={label} icon={[<InfoOutlined />, <PaymentsOutlined />, <RequestQuote />, <FolderOutlined />][index]}
                                                id={`opportunity-tab-${index}`} aria-controls={`opportunity-panel-${index}`} />)}
                                    </Tabs>
                                </Box>
                                {panels.map((panel, index) => <Box key={index} role="tabpanel" hidden={tab !== index}
                                    id={`opportunity-panel-${index}`} aria-labelledby={`opportunity-tab-${index}`}>
                                    {panel}
                                </Box>)}
                            </Box>}
                        </div>
                    </>
                </FormProvider>
            }
        />
    )
}

export default BaseSingleOpportunity
